/* eslint-disable react/require-default-props */
import { Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { getStorage, setStorage, StorageTypes } from '@helpers/storage';

import LabelTree from './label_tree';
import './styles/main.scss';
import ToggleDrawerButton from './toggle_drawer_button';

const LabelsDrawer = (props) => {
  const [expandedLabels, setExpandedLabels] = useState({});

  const {
    activateLabel,
    activeLabelKey,
    labels,
    filtersDrawerOpen,
    selectedAssetKeys,
    toggleFiltersDrawer
  } = props;
  const resourceName = BF.fx.resource().name;
  const activeClass = activeLabelKey ? '' : 'active';
  const labelsDrawerClass = filtersDrawerOpen === 'labels' ? 'open' : '';
  const isDrawerOpen = filtersDrawerOpen === 'labels';
  const labelsDrawerPanelId = 'labels-drawer-panel';
  const labelsDrawerButtonId = 'labels-drawer-button';
  const labelsDrawerStickyButtonId = 'labels-drawer-button-sticky';

  const setPreviousLabels = () => {
    const drawerOpenLocal = getStorage(StorageTypes.Local, 'drawerOpen');
    const labelsDrawerOpenLocal = drawerOpenLocal ? drawerOpenLocal === 'labels' : getStorage(StorageTypes.Local, 'labelsDrawerOpen') === true;
    const expandedLabelsLocal = getStorage(StorageTypes.Local, 'expandedLabels');

    if (labelsDrawerOpenLocal) {
      toggleFiltersDrawer('labels-drawer');

      if (expandedLabelsLocal && Object.keys(expandedLabelsLocal).length) {
        setExpandedLabels(expandedLabelsLocal);
      }
    }
  };

  const toggleExpand = (key, removeExistingKey = true, toggleState) => {
    const updatedExpandedLabels = { ...expandedLabels };

    if (updatedExpandedLabels[key] && removeExistingKey && !toggleState) {
      delete updatedExpandedLabels[key];
    } else if (!updatedExpandedLabels[key]) {
      updatedExpandedLabels[key] = true;
    }

    setStorage(StorageTypes.Local, 'expandedLabels', updatedExpandedLabels);
    setExpandedLabels(updatedExpandedLabels);
  };

  const toggleExpandForActiveLabel = (key) => {
    const activeLabelPath = [...labels.labelsFlat[key].path];

    if (activeLabelPath.length > 1) {
      activeLabelPath.pop(); // remove key for activeLabel so only parents are expanded
      activeLabelPath.forEach((labelKey) => { toggleExpand(labelKey, false); });
    }
  };

  useEffect(() => {
    setPreviousLabels();
  }, []);

  useEffect(() => {
    if (activeLabelKey) {
      toggleExpandForActiveLabel(activeLabelKey);
    }
  }, [activeLabelKey]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Avoid closing the drawer when Escape is meant for a dialog/modal.
      if (event.target instanceof Element && event.target.closest('[role="dialog"], .modal, .ReactModal__Content')) {
        return;
      }

      event.preventDefault();
      toggleFiltersDrawer('labels-drawer');

      const stickyToggle = document.getElementById(labelsDrawerStickyButtonId);
      const sideToggle = document.getElementById(labelsDrawerButtonId);
      (stickyToggle || sideToggle)?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, toggleFiltersDrawer]);

  const onLabelSelect = (key) => {
    if (key === activeLabelKey) { return undefined; }

    const newKey = key === 'resource' ? null : key;
    activateLabel(newKey);
    return undefined;
  };

  return (
    <section className={`labels-drawer-container ${labelsDrawerClass}`} data-cx-customization={`labels-drawer-${BFG.resource.type}`}>
      <div className={`labels-drawer-container-maxheight bf-scroll-element ${selectedAssetKeys.size ? 'select-bar-active' : ''}`}>
        <div className="labels-toggle-tab">
          <ToggleDrawerButton
            ariaControls={labelsDrawerPanelId}
            ariaDisabled={false}
            ariaExpanded={isDrawerOpen}
            ariaLabelledBy="labels-drawer-heading"
            id={labelsDrawerButtonId}
            toggleFiltersDrawer={toggleFiltersDrawer}
          >
            <span aria-hidden="true" className="drawer-title modal-label"><Trans>Labels</Trans></span>
          </ToggleDrawerButton>
        </div>
        <div className="labels-expand-container bf-sticky-element">
          <h2 className="drawer-title modal-label" id="labels-drawer-heading"><Trans>Labels</Trans></h2>
          <ToggleDrawerButton
            ariaControls={labelsDrawerPanelId}
            ariaDisabled={false}
            ariaExpanded={isDrawerOpen}
            ariaLabelledBy="labels-drawer-heading"
            id={labelsDrawerStickyButtonId}
            toggleFiltersDrawer={toggleFiltersDrawer}
          />
        </div>
        <div className={`label-item ${activeClass}`}>
          <div className="label-name-container">
            <h2 className="name-copy" title={resourceName}>
              <button
                className="select-button"
                onClick={() => onLabelSelect('resource')}
                type="button"
              >
                {resourceName}
              </button>
            </h2>
          </div>
        </div>
        {/* Keep the controlled panel in the DOM so aria-controls remains valid when collapsed. */}
        <div
          aria-labelledby={labelsDrawerButtonId}
          hidden={!isDrawerOpen}
          id={labelsDrawerPanelId}
          role="region"
        >
          {isDrawerOpen && (
            <LabelTree
              activeLabelKey={activeLabelKey}
              expandedLabels={expandedLabels}
              labelsTree={labels.labelsTree}
              onLabelSelect={onLabelSelect}
              toggleExpand={toggleExpand}
            />
          )}
        </div>
      </div>
    </section>
  );
};

LabelsDrawer.propTypes = {
  activateLabel: PropTypes.func.isRequired,
  activeLabelKey: PropTypes.string,
  labels: PropTypes.shape({}),
  filtersDrawerOpen: PropTypes.oneOf(['labels', 'advanced-filters', 'none']).isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
  toggleFiltersDrawer: PropTypes.func.isRequired,
};

LabelsDrawer.defaultProps = {
  activeLabelKey: null,
  labels: {},
};

export default LabelsDrawer;
